import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '../InputLabel';
import Feedback from '../Feedback';
import StackView from '../StackView';
import { useThemeTokens } from '../ThemeProvider';
import useInputSupports from './useInputSupports';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InputSupports = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    copy = 'en',
    label,
    hint,
    hintPosition = 'inline',
    feedback,
    tooltip,
    validation,
    nativeID
  } = _ref;
  const {
    space
  } = useThemeTokens('InputSupports');
  const {
    inputId,
    hintId,
    feedbackId,
    a11yProps
  } = useInputSupports({
    feedback,
    hint,
    label,
    validation,
    nativeID
  });
  return /*#__PURE__*/_jsxs(StackView, {
    space: space,
    ref: ref,
    children: [label && /*#__PURE__*/_jsx(InputLabel, {
      copy: copy,
      label: label,
      hint: hint,
      hintPosition: hintPosition,
      hintId: hintId,
      tooltip: tooltip,
      forId: inputId
    }), typeof children === 'function' ? children({
      inputId,
      ...a11yProps
    }) : children, feedback && /*#__PURE__*/_jsx(Feedback, {
      id: feedbackId,
      title: feedback,
      validation: validation
    })]
  });
});
InputSupports.displayName = 'InputSupports';
InputSupports.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),

  /**
   * Whether the English or French copy will be used (e.g. for accessibility labels).
   */
  copy: PropTypes.oneOf(['en', 'fr']),

  /**
   * The input label.
   */
  label: PropTypes.string,

  /**
   * A short description of the expected input.
   */
  hint: PropTypes.string,

  /**
   * Position of the hint relative to label.
   */
  hintPosition: PropTypes.oneOf(['inline', 'below']),

  /**
   * A detailed description of validation error/success or additional instructions.
   * Visual variant is determined based on the `validation` prop.
   */
  feedback: PropTypes.string,

  /**
   * Content of an optional `Tooltip`. If set, a tooltip button will be shown next to the label.
   */
  tooltip: PropTypes.string,

  /**
   * Use to visually mark an input as valid or invalid.
   */
  validation: PropTypes.oneOf(['error', 'success']),

  /**
   * ID for DOM element on web
   */
  nativeID: PropTypes.string
};
export default InputSupports;